import React, { useState, useEffect, useRef } from "react";
import toast from "react-hot-toast";
import { env } from "../../config/env";

/**
 * Google Maps location picker — active map provider.
 * Mappls (MapmyIndia) picker preserved in mapplsMapPicker.jsx for future rollback.
 * To switch back to Mappls, comment GoogleMapPicker usage in personalInfoPage,
 * workExperiencePage, and educationPage, then uncomment MapplsMapPicker there.
 */
export const GoogleMapPicker = ({ show, onClose, onSelect, initialSearch = "" }) => {
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const markerRef = useRef(null);
    const searchBoxRef = useRef(null);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [loading, setLoading] = useState(true);
    const GOOGLE_MAPS_API_KEY = env.googleMapsApiKey;

    useEffect(() => {
        if (!show) return;

        if (!GOOGLE_MAPS_API_KEY) {
            setLoading(false);
            toast.error("Google Maps API key is not configured");
            return;
        }

        window.requestAnimationFrame(() => {
            if (!window.google) {
                loadGoogleMapsScript();
            } else {
                initializeMap();
            }
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    const loadGoogleMapsScript = () => {
        if (window.google) {
            initializeMap();
            return;
        }

        const script = document.createElement("script");
        script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
        script.async = true;
        script.defer = true;
        script.onload = () => {
            setLoading(false);
            setTimeout(() => initializeMap(), 500);
        };
        script.onerror = () => {
            setLoading(false);
            toast.error("Failed to load Google Maps");
            };
        document.head.appendChild(script);
    };

    const initializeMap = () => {
        if (!mapRef.current || !window.google) {
            return;
        }

        const defaultLocation = { lat: 20.5937, lng: 78.9629 }; // India center

        try {
            const map = new window.google.maps.Map(mapRef.current, {
                zoom: 12,
                center: defaultLocation,
                mapTypeControl: false,
                streetViewControl: false,
                fullscreenControl: false,
                zoomControl: true,
            });

            mapInstanceRef.current = map;
            setLoading(false);
            window.requestAnimationFrame(() => {
                window.google.maps.event.trigger(map, "resize");
                map.setCenter(defaultLocation);
            });

            // If a pre-filled search term was passed, geocode it and pan map there
            if (initialSearch) {
                const searchInput = document.getElementById("map-search-box");
                if (searchInput) searchInput.value = initialSearch;

                const geocoder = new window.google.maps.Geocoder();
                geocoder.geocode({ address: initialSearch }, (results, status) => {
                    if (status === "OK" && results[0]) {
                        const loc = results[0].geometry.location;
                        const lat = loc.lat();
                        const lng = loc.lng();
                        map.setCenter({ lat, lng });
                        map.setZoom(15);
                        placeMarker(lat, lng, map);
                        reverseGeocode(lat, lng);
                    }
                });
            }

            // Add click listener to map. If a POI/placeId was clicked, fetch its details
            // so we can use the official place name (e.g. "GJ5 Fashion") when available.
            map.addListener("click", (e) => {
                const lat = e.latLng.lat();
                const lng = e.latLng.lng();

                // If the click includes a Google Place ID, prefer fetching the place
                // details (name) via PlacesService so we can populate `place_name`.
                if (e.placeId && window.google.maps.places) {
                    // Prevent the default info window from showing
                    if (typeof e.stop === 'function') e.stop();

                    const placesService = new window.google.maps.places.PlacesService(map);
                    placesService.getDetails(
                        { placeId: e.placeId, fields: ["name", "geometry", "formatted_address", "place_id"] },
                        (place, status) => {
                            if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
                                const placeLat = place.geometry && place.geometry.location ? place.geometry.location.lat() : lat;
                                const placeLng = place.geometry && place.geometry.location ? place.geometry.location.lng() : lng;
                                const placeName = place.name || "";
                                placeMarker(placeLat, placeLng, map);
                                reverseGeocode(placeLat, placeLng, placeName);
                            } else {
                                // Fallback to regular reverse geocode
                                placeMarker(lat, lng, map);
                                reverseGeocode(lat, lng);
                            }
                        }
                    );
                } else {
                    placeMarker(lat, lng, map);
                    reverseGeocode(lat, lng);
                }
            });

            // Setup search box
            const searchInput = document.getElementById("map-search-box");
            if (searchInput && window.google.maps.places) {
                const searchBox = new window.google.maps.places.SearchBox(searchInput);
                searchBoxRef.current = searchBox;

                searchBox.addListener("places_changed", () => {
                    const places = searchBox.getPlaces();
                    if (places.length === 0) return;

                    const place = places[0];
                    if (!place.geometry || !place.geometry.location) return;

                    const lat = place.geometry.location.lat();
                    const lng = place.geometry.location.lng();

                    placeMarker(lat, lng, map);
                    map.setCenter({ lat, lng });
                    map.setZoom(16);

                    // Get detailed address — pass place name for company auto-fill
                    reverseGeocode(lat, lng, place.name || "");
                });

                // Bias search to visible region
                map.addListener("bounds_changed", () => {
                    searchBox.setBounds(map.getBounds());
                });
            }
        } catch (error) {
            toast.error("Error initializing map");
        }
    };

    const placeMarker = (lat, lng, map) => {
        // Remove old marker
        if (markerRef.current) {
            markerRef.current.setMap(null);
        }

        // Add new marker
        const marker = new window.google.maps.Marker({
            position: { lat, lng },
            map: map,
            title: "Selected Location",
            draggable: true,
            icon: "http://maps.google.com/mapfiles/ms/icons/red-dot.png",
        });

        // Update on drag
        marker.addListener("dragend", () => {
            const newLat = marker.getPosition().lat();
            const newLng = marker.getPosition().lng();
            reverseGeocode(newLat, newLng);
        });

        markerRef.current = marker;
    };

    const reverseGeocode = (lat, lng, placeName = "") => {
        if (!window.google) return;

        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: { lat, lng } }, (results, status) => {
            if (status === "OK" && results[0]) {
                const result = results[0];
                const formattedAddress = result.formatted_address;

                // Extract address components
                const addressComponents = {};
                result.address_components.forEach((component) => {
                    const type = component.types[0];
                    addressComponents[type] = component.long_name;
                });
                const isPlusCodePart = (value = "") => /^[A-Z0-9]{4,}\+[A-Z0-9]{2,}/i.test(String(value).trim());
                const addressParts = formattedAddress.split(",").map(p => p.trim()).filter(Boolean);
                const usableAddressParts = addressParts.filter((part) => !isPlusCodePart(part));
                
                // Initialize variables
                let flatNumber = "";
                let buildingName = "";
                let streetName = "";

                // Pattern to match flat numbers: "2/3", "D 102", "A-101", "Apt 5", etc.
                const flatPattern = /^([0-9]+\/[0-9]+|[A-Z]\s*\d+|[A-Z]\-\d+|Apt\s*\d+|Unit\s*\d+|#\d+)$/i;
                
                // Check if first part matches flat number pattern
                const firstUsablePart = usableAddressParts[0] || "";
                const secondUsablePart = usableAddressParts[1] || "";

                if (flatPattern.test(firstUsablePart)) {
                    flatNumber = firstUsablePart;
                    // Building name is everything from 2nd part onwards until we reach city
                    const cityIndex = usableAddressParts.findIndex(p => 
                        p === addressComponents.locality || 
                        p.toLowerCase().includes(addressComponents.locality?.toLowerCase())
                    );
                    
                    if (cityIndex > 1) {
                        buildingName = usableAddressParts.slice(1, cityIndex).join(", ");
                    } else if (secondUsablePart) {
                        buildingName = secondUsablePart;
                    }
                    streetName = buildingName ? `${flatNumber}, ${buildingName}` : flatNumber;
                } else {
                    // First part is building/street name
                    const routeName = addressComponents.route || "";
                    buildingName =
                        routeName && firstUsablePart.toLowerCase() === routeName.toLowerCase()
                            ? ""
                            : firstUsablePart || "";
                    streetName = routeName || firstUsablePart || "";
                    
                    // Look for flat number in second part
                    if (secondUsablePart && flatPattern.test(secondUsablePart)) {
                        flatNumber = secondUsablePart;
                        streetName = `${buildingName}, ${flatNumber}`;
                    }
                }

                if (isPlusCodePart(flatNumber)) flatNumber = "";
                if (isPlusCodePart(buildingName)) buildingName = "";
                if (isPlusCodePart(streetName)) streetName = addressComponents.route || "";

                // Derive a usable place name: prefer explicit `placeName` passed from SearchBox,
                // then `result.name` from geocoder (if present), then plausible address components,
                // then building/street/fallback to first formatted address part.
                const derivePlaceName = () => {
                    const p = (placeName || "").trim();
                    if (p) return p;
                    if (result.name && result.name.toString().trim()) return result.name.toString().trim();
                    const nameCandidates = [
                        'establishment',
                        'premise',
                        'point_of_interest',
                        'neighborhood',
                        'sublocality_level_1',
                        'sublocality',
                    ];
                    for (const t of nameCandidates) {
                        if (addressComponents[t] && addressComponents[t].toString().trim()) {
                            return addressComponents[t].toString().trim();
                        }
                    }
                    if (buildingName && buildingName.trim()) return buildingName.trim();
                    if (streetName && streetName.trim()) return streetName.trim();
                    if (firstUsablePart && firstUsablePart.trim()) return firstUsablePart.trim();
                    return formattedAddress || "";
                };

                const locationData = {
                    formatted_address: formattedAddress,
                    latitude: lat,
                    longitude: lng,
                    pin_code: addressComponents.postal_code || "",
                    street_name: streetName,
                    city_region_country: `${addressComponents.locality || ""}, ${addressComponents.administrative_area_level_1 || ""}, ${addressComponents.country || ""}`.replace(/^,\s*|,\s*$/g, ""),
                    building_name: buildingName,
                    flat_number: flatNumber,
                    google_map_link: `https://maps.google.com/?q=${lat},${lng}`,
                    place_name: derivePlaceName(),
                    // Individual address components for per-field mapping
                    city: addressComponents.locality || "",
                    region: addressComponents.administrative_area_level_1 || "",
                    country: addressComponents.country || "",
                    area_locality: addressComponents.sublocality_level_1 || addressComponents.sublocality || addressComponents.neighborhood || "",
                };

                setSelectedLocation(locationData);
            } else {
                toast.error("Could not get address from location");
            }
        });
    };

    const handleConfirm = () => {
        if (selectedLocation) {
            onSelect(selectedLocation);
            setSelectedLocation(null);
            onClose();
        } else {
            toast.error("Please select a location by clicking on the map or searching");
        }
    };

    if (!show) return null;

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-3 sm:p-4 pb-[calc(var(--mobile-nav-offset,5rem)+0.75rem)] sm:pb-4">
            <div className="flex max-h-full w-full max-w-6xl flex-col overflow-hidden rounded-3xl bg-white p-4 shadow-2xl sm:h-[95vh] sm:p-6">
                {/* Header */}
                <div className="mb-3 flex shrink-0 items-start justify-between gap-3 sm:mb-4">
                    <div className="min-w-0">
                        <h2 className="text-xl font-bold text-gray-900 sm:text-2xl">Select Location</h2>
                        <p className="mt-1 text-sm text-gray-500">Click on map to pin location or search for an address</p>
                    </div>
                    <button
                        type="button"
                        className="shrink-0 text-2xl leading-none text-gray-400 transition hover:text-gray-600"
                        onClick={onClose}
                        aria-label="Close"
                    >
                        ✕
                    </button>
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch]">
                    {/* Search Input */}
                    <div className="mb-3 sm:mb-4">
                        <input
                            id="map-search-box"
                            type="text"
                            placeholder="Search location or address..."
                            className="w-full rounded-xl border border-gray-300 bg-white py-3 pl-4 pr-4 text-gray-800 shadow-md outline-none transition placeholder:text-gray-400 focus:border-[#7C4DFF] focus:ring-4 focus:ring-[#7C4DFF]/15"
                        />
                    </div>

                    {/* Map Container */}
                    <div className="relative mb-3 h-[200px] min-h-[200px] shrink-0 overflow-hidden rounded-2xl border-2 border-gray-200 sm:mb-4 sm:h-[280px] sm:min-h-[280px] md:h-[320px] md:min-h-[320px]">
                        {loading && (
                            <div className="absolute inset-0 z-40 flex items-center justify-center bg-white/80">
                                <div className="text-center">
                                    <div className="mx-auto mb-3 h-12 w-12 animate-spin rounded-full border-4 border-[#6938EF] border-t-transparent" />
                                    <p className="font-medium text-gray-600">Loading Google Maps...</p>
                                </div>
                            </div>
                        )}
                        <div ref={mapRef} className="h-full w-full" />
                    </div>

                    {/* Selected Location Details */}
                    {selectedLocation && (
                        <div className="mb-3 shrink-0 rounded-xl border border-[#E6E2FF] bg-gradient-to-r from-[#6938EF]/5 to-transparent p-4 sm:mb-4">
                            <div className="grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-2">
                                <div>
                                    <p className="mb-1 text-sm font-semibold text-gray-700">Address</p>
                                    <p className="text-sm text-gray-600">{selectedLocation.formatted_address}</p>
                                </div>
                                {selectedLocation.pin_code && (
                                    <div>
                                        <p className="mb-1 text-sm font-semibold text-gray-700">Pin Code</p>
                                        <p className="text-sm text-gray-600">{selectedLocation.pin_code}</p>
                                    </div>
                                )}
                                {selectedLocation.city_region_country && (
                                    <div>
                                        <p className="mb-1 text-sm font-semibold text-gray-700">City/Region</p>
                                        <p className="text-sm text-gray-600">{selectedLocation.city_region_country}</p>
                                    </div>
                                )}
                                {selectedLocation.street_name && (
                                    <div>
                                        <p className="mb-1 text-sm font-semibold text-gray-700">Street</p>
                                        <p className="text-sm text-gray-600">{selectedLocation.street_name}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Action Buttons — always pinned below scroll area */}
                <div className="flex shrink-0 gap-3 border-t border-gray-100 pt-3 sm:pt-4">
                    <button
                        type="button"
                        onClick={onClose}
                        className="flex-1 rounded-full border-2 border-gray-300 px-4 py-3 font-semibold text-gray-700 transition hover:bg-gray-50 sm:px-6"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleConfirm}
                        disabled={!selectedLocation}
                        className="submit-button flex-1 rounded-full bg-[#7C4DFF] px-4 py-3 font-semibold text-white transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50 sm:px-6"
                    >
                        Confirm Location
                    </button>
                </div>
            </div>
        </div>
    );
};
